import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Vennligst fyll ut alle felt");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passordene må være like");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Passordet må være minst 6 tegn");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });

      const { id, name, email, token } = response.data;
      login({ id, name, email }, token);
      toast.success("Konto opprettet!");
      navigate("/");
    } catch (error) {
      console.error("Registration error:", error);
      const message = error.response?.data?.detail || "Kunne ikke opprette konto";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div 
            className="flex justify-center mb-4 cursor-pointer" 
            onClick={() => navigate("/")}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_golfbazar/artifacts/d3dy0w6h_Skjermbilde%202025-12-20%20125648.png" 
              alt="GolfMarked Logo" 
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold">Opprett konto</CardTitle>
          <CardDescription>
            Registrer deg for å legge ut annonser
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Navn</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Ditt navn"
                className="mt-1"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="din@email.no"
                className="mt-1"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Passord</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                placeholder="Minst 6 tegn"
                className="mt-1"
                data-testid="register-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Bekreft passord</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange("confirmPassword", e.target.value)}
                placeholder="Gjenta passordet"
                className="mt-1"
                data-testid="register-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? "Oppretter..." : "Opprett konto"}
            </Button>
            <p className="text-xs text-gray-500 text-center mt-3">
              Ved å registrere deg godtar du vår{" "}
              <Link to="/privacy" className="text-emerald-600 hover:underline">
                personvernerklæring
              </Link>
            </p>
          </form>
          <p className="text-center text-sm text-gray-600 mt-4">
            Har du allerede en konto?{" "}
            <Link to="/login" className="text-emerald-600 hover:underline">
              Logg inn
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
